import Axios, { AxiosError, AxiosRequestConfig } from "axios";
import qs from "qs";

import config from "app.config";
import AuthService from "utils/authService";
import { Mutex, tryAcquire } from "async-mutex";

let csrfToken: string | undefined;

export function clearCsrfToken() {
  csrfToken = undefined;
}

let invitationId: string | undefined;
export function setInvitationId(id: string) {
  invitationId = id;
}

let openInvitationId: string | undefined;
export function setOpenInvitationId(id: string) {
  openInvitationId = id;
}

// Set the base URL to the configured API url, and also append the Spring Boot context-root.
export const AXIOS_INSTANCE = Axios.create({
  baseURL: config.apiUrl || "",
  paramsSerializer: {
    serialize: (params) =>
      qs.stringify(params, {
        arrayFormat: "repeat",
        encode: true,
      }),
    indexes: null,
  },
  withCredentials: true,
});

// Create a separate instance of Axios to perform the security requests required by the base
// instance (otherwise, if the base instance used itself to make security requests it would
// enter an infinite loop).
const AXIOS_SECURITY_INSTANCE = Axios.create({
  baseURL: config.apiUrl || "",
  withCredentials: true,
});

const csrfMutext = new Mutex();
AXIOS_INSTANCE.interceptors.request.use(async (request) => {
  if (!csrfToken && request.method?.toLowerCase() !== "get") {
    csrfToken = await csrfMutext.runExclusive(async () => {
      if (csrfToken) {
        return csrfToken;
      }
      return await getCsrfToken();
    });
  }

  request.headers.set("Diverst-UserToken", AuthService.getJwt());

  if (
    !request.headers.has("content-type") &&
    !(request.data instanceof FormData)
  ) {
    request.headers.set("content-type", "application/json");
  }
  if (request.method?.toLowerCase() !== "get" && csrfToken) {
    request.headers.set("X-CSRF-TOKEN", csrfToken);
  }
  if (openInvitationId) {
    request.headers.set("Network-Open-Invitation-Id", openInvitationId);
  }
  if (invitationId) {
    request.headers.set("Network-Invitation-Id", invitationId);
  }

  return request;
});

AXIOS_INSTANCE.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Clear the CSRF token if forbidden, as an invalid CSRF token will trigger a 403
    if (error.response?.status === 403) {
      csrfToken = undefined;
    }
    return Promise.reject(error);
  },
);

async function getCsrfToken(): Promise<string> {
  return AXIOS_SECURITY_INSTANCE.get("/api/v2/internal/csrf-token", {
    headers: { "Diverst-UserToken": AuthService.getJwt() },
  })
    .then((resp) => resp.headers["csrf-token"] as string)
    .catch(() => "");
}

/**
 * Ends the Zitadel session as well as this application's, and answers with the provider's logout
 * URL for the browser to follow.
 *
 * <p>Must run before {@link legacyLogout}: that call invalidates the session row this request
 * authenticates with, so the other order means this one arrives unauthenticated and the provider's
 * session is never ended at all.
 *
 * Resolves to undefined whenever there is nothing to follow - the feature is off, the login did not
 * come from Zitadel, or the call failed. A logout must never be blocked by it.
 */
export async function performOidcLogout(): Promise<string | undefined> {
  if (!config.cliqIamEnabled) {
    return undefined;
  }
  return customInstance<{ logoutUrl?: string | null }>({
    method: "POST",
    url: "/api/v2/auth/oidc/logout",
  })
    .then((data) => data.logoutUrl ?? undefined)
    .catch(() => undefined);
}

/**
 * Where to send the browser so it arrives at a sibling product already signed in.
 *
 * <p>A link straight at the peer's own URL is enough for a customer's own user - the peer starts
 * its own Zitadel login and the browser's existing provider session satisfies it silently. It is
 * not enough for staff, because every product pins its login to one Zitadel organization and a
 * staff member belongs to none of the customer organizations it would be pinned to; nor for anyone
 * whose session here was minted by the auth-handoff, which Zitadel never saw and so may leave no
 * provider session to ride on. Asking the server to arrange the session sidesteps both.
 *
 * Undefined whenever no session could be arranged - unconfigured peer, unreachable peer, or no
 * single active user there for this address. The caller must then link at the peer directly, which
 * is what the link did before this existed.
 */
export async function fetchPeerLaunchUrl(
  peer: string,
): Promise<string | undefined> {
  return customInstance<{ redirectUrl?: string | null }>({
    method: "GET",
    url: `/api/v2/auth/handoff/launch/${peer}`,
  })
    .then((data) => data.redirectUrl ?? undefined)
    .catch(() => undefined);
}

const logoutMutex = new Mutex();
export async function performLogout() {
  return tryAcquire(logoutMutex)
    .runExclusive(async () =>
      customInstance({ method: "POST", url: "/api/v2/logout" }).then(() => {
        csrfToken = undefined;
      }),
    )
    .catch(() => {
      /* no op */
    });
}

// See https://orval.dev/guides/custom-axios
// Declared as a function rather than a generic arrow: babel-plugin-formatjs enables the
// JSX parser plugin for every file, so .ts sources are parsed as TSX and `<T>(...) =>`
// would be read as a JSX element.
export function customInstance<T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> {
  const source = Axios.CancelToken.source();
  const promise = AXIOS_INSTANCE({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(
    ({ data }) => data as T, // Unwrap the data
  );

  // @ts-expect-error - Not enough typing information on Axios
  promise.cancel = () => {
    source.cancel("Query was cancelled");
  };

  return promise;
}

// This export needs to be present, otherwise Orval defaults to "unknown" for the error type
export type ErrorType<Error> = AxiosError<Error>;
